import { FC, useEffect, useState, useCallback } from 'react'
import {
	Table,
	Button,
	Select,
	Tag,
	Popconfirm,
	message,
	Space,
	Tooltip,
	Badge
} from 'antd'
import { PlusOutlined, DeleteOutlined, CameraOutlined } from '@ant-design/icons'
import { Inspection } from '../helpers/types/inventory'
import {
	fetchInspections,
	deleteInspection
} from '../api/inventory'
import { carrierStore } from '../store/carrier'
import { Link, useNavigate } from 'react-router-dom'
import { RNCarriers } from '../helpers/routeNames'

const InventoryInspections: FC = () => {
	const { carriers } = carrierStore()
	const navigate = useNavigate()
	const [carrierId, setCarrierId] = useState<number | null>(null)
	const [inspections, setInspections] = useState<Inspection[]>([])
	const [loading, setLoading] = useState(false)

	const load = useCallback(async (cId: number) => {
		setLoading(true)
		try {
			const data = await fetchInspections(cId)
			setInspections(data)
		} catch {
			message.error('Failed to load inspections')
		} finally {
			setLoading(false)
		}
	}, [])

	useEffect(() => {
		if (carrierId) load(carrierId)
		else setInspections([])
	}, [carrierId, load])

	const handleDelete = async (id: number) => {
		try {
			await deleteInspection(id)
			setInspections((prev) => prev.filter((i) => i.id !== id))
		} catch {
			message.error('Error')
		}
	}

	const columns = [
		{
			title: 'Carrier',
			key: 'carrier',
			render: () => {
				const c = carriers.find((c) => c.id === carrierId)
				return c ? (
					<Link to={`${RNCarriers}/${c.id}#inventory-inspections`}>
						{c.name}
					</Link>
				) : '—'
			}
		},
		{
			title: 'Date',
			dataIndex: 'inspectedAt',
			key: 'inspectedAt',
			render: (v: string) =>
				new Date(v).toLocaleDateString('ru', {
					day: 'numeric',
					month: 'short',
					year: 'numeric'
				})
		},
		{
			title: 'Inspector',
			dataIndex: 'inspectedBy',
			key: 'inspectedBy',
			render: (v: string) => <Tag color="blue">{v}</Tag>
		},
		{
			title: 'Photos',
			key: 'photos',
			render: (_: any, r: Inspection) => (
				<Space size={4}>
					<CameraOutlined style={{ color: '#3894EB' }} />
					<Badge
						count={r.photos.length}
						style={{ backgroundColor: '#3894EB' }}
						showZero
					/>
				</Space>
			)
		},
		{
			title: 'Notes',
			dataIndex: 'notes',
			key: 'notes',
			ellipsis: true,
			render: (v: string | null) => v || '—'
		},
		{
			title: '',
			key: 'actions',
			width: 60,
			render: (_: any, rec: Inspection) => (
				<Popconfirm
					title="Delete this inspection and all photos?"
					onConfirm={() => handleDelete(rec.id)}
					okButtonProps={{ danger: true }}
					okText="Delete">
					<Button
						icon={<DeleteOutlined />}
						size="small"
						type="text"
						danger
					/>
				</Popconfirm>
			)
		}
	]

	return (
		<div>
			<header className="page-header">
				<h1 className="page-h1">Field Inspections</h1>
			</header>
			<main>
				<Space
					style={{ marginBottom: 16, display: 'flex', flexWrap: 'wrap' }}
					size={8}>
					<Select
						placeholder="Select carrier"
						style={{ width: 220 }}
						allowClear
						showSearch
						optionFilterProp="label"
						value={carrierId}
						onChange={(v) => setCarrierId(v ?? null)}
						options={carriers.map((c) => ({
							label: c.name,
							value: c.id
						}))}
					/>
					<Tooltip title={!carrierId ? 'Select a carrier first' : ''}>
						<Button
							type="primary"
							icon={<PlusOutlined />}
							disabled={!carrierId}
							onClick={() =>
								carrierId &&
								navigate(`${RNCarriers}/${carrierId}#inventory-inspections`)
							}>
							New Inspection
						</Button>
					</Tooltip>
				</Space>

				<Table
					dataSource={inspections}
					columns={columns}
					rowKey="id"
					loading={loading}
					pagination={{ pageSize: 20 }}
					locale={{
						emptyText: carrierId
							? 'No inspections'
							: 'Select a carrier to view inspections'
					}}
				/>
			</main>
		</div>
	)
}

export default InventoryInspections
